// frontend/src/components/parametres/LicenceSection.jsx
// ============================================================================
// SECTION AFFICHAGE LICENCE — v2
// ============================================================================

import React, { useEffect } from 'react';
import {
  Calendar, Users, UserCog, CheckCircle, XCircle,
  AlertCircle, Shield, Key, Zap, Tag,
  Info,
} from 'lucide-react';
import { useLicence } from '../hooks/useLicence';
import { useLoader } from '../contexts/LoaderContext';

const LicenceSection = () => {
  const { licence, loading, error, getStatutLicence } = useLicence();
  const { showLoader, hideLoader } = useLoader();

  useEffect(() => {
    if (loading) showLoader();
    else hideLoader();
  }, [loading]);

  if (loading) return null;

  if (error || !licence) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
        <div className="flex items-center gap-3 text-red-600">
          <XCircle size={24} />
          <div>
            <h3 className="font-semibold">Erreur de chargement</h3>
            <p className="text-sm text-gray-600 mt-1">
              {error || 'Impossible de récupérer les informations de licence'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ── Helpers ──
  const getPct = (val, max) => max === 0 ? 0 : Math.min(Math.round((val / max) * 100), 100);

  const getBarColor = (pct) => {
    if (pct >= 100) return '#ef4444';
    if (pct >= 80)  return '#f59e0b';
    return '#448696';
  };

  const getJoursColor = (jours) => {
    if (jours <= 7)  return '#ef4444';
    if (jours <= 30) return '#f59e0b';
    return '#448696';
  };

  const getTypeLicenceLabel = (type) => {
    return { basique: 'Basique', standard: 'Standard', premium: 'Premium' }[type] || type;
  };

  const getTypeLicenceColor = (type) => {
    return { basique: '#448696', standard: '#448696', premium: '#448696' }[type] || '#448696';
  };

  const formatDate = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });
  };

  const statutInfo = getStatutLicence();
  const typeColor  = getTypeLicenceColor(licence.type_licence);

  return (
    <div className="space-y-5">

      {/* ── Bandeau principal ── */}
      <div className="rounded-2xl overflow-hidden shadow-sm border border-gray-100">
        {/* Header coloré */}
        <div style={{ background: `linear-gradient(135deg, ${typeColor}, ${typeColor}cc)` }}
          className="px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-4">
          
            <div>
              <p className="text-white/80 text-xs font-semibold uppercase tracking-wider mb-0.5">
                Licence HoraPilot
              </p>
              <h2 className="text-white text-xl font-black">
                {getTypeLicenceLabel(licence.type_licence)}
              </h2>
            </div>
          </div>
          <div className="text-right">
            <div className="inline-flex items-center gap-2 bg-white/20 px-3 py-1.5 rounded-lg mb-1">
              <Info size={13} className="text-white" />
              <span className="text-white text-xs font-bold">VERSION {licence.version_app || '1.0.0'}</span>
            </div>
            <div className="flex items-center justify-end gap-1.5">
              <div className={`w-2 h-2 rounded-full ${
                licence.statut === 'active' ? 'bg-green-400' :
                licence.statut === 'expire_bientot' ? 'bg-yellow-400' : 'bg-red-400'
              }`} />
              <span className="text-white/90 text-xs font-semibold capitalize">
                {licence.statut === 'active' ? 'Active' :
                 licence.statut === 'expire_bientot' ? 'Expire bientôt' :
                 licence.statut === 'expiree' ? 'Expirée' : licence.statut}
              </span>
            </div>
          </div>
        </div>

        {/* Infos sous le header */}
        <div className="bg-white px-6 py-4 grid grid-cols-3 divide-x divide-gray-100">
          <div className="pr-6">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">N° Licence</p>
            <p className="font-mono text-sm font-bold text-gray-900">{licence.numero_licence}</p>
          </div>
          <div className="px-6">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Expire le</p>
            <p className="text-sm font-bold text-gray-900">{formatDate(licence.date_fin)}</p>
          </div>
          <div className="pl-6">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Jours restants</p>
            <p className="text-sm font-black" style={{ color: getJoursColor(licence.jours_restants) }}>
              {licence.jours_restants} jours
            </p>
          </div>
        </div>
      </div>

      {/* ── Utilisation ── */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
        <h3 className="text-base font-bold text-gray-900 mb-5 flex items-center gap-2">
          <Tag size={16} style={{ color: '#448696' }} />
          Utilisation
        </h3>

        <div className="space-y-5">

          {/* Utilisateurs */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg flex items-center justify-center"
                  style={{ background: '#44869615' }}>
                  <UserCog size={15} style={{ color: '#448696' }} />
                </div>
                <span className="text-sm font-semibold text-gray-700">Utilisateurs</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-black text-gray-900">
                  {licence.nb_utilisateurs_actifs}
                </span>
                <span className="text-xs text-gray-400">/ {licence.max_utilisateurs}</span>
                <span className="text-xs font-bold px-2 py-0.5 rounded-full"
                  style={{
                    background: getBarColor(getPct(licence.nb_utilisateurs_actifs, licence.max_utilisateurs)) + '15',
                    color: getBarColor(getPct(licence.nb_utilisateurs_actifs, licence.max_utilisateurs))
                  }}>
                  {getPct(licence.nb_utilisateurs_actifs, licence.max_utilisateurs)}%
                </span>
              </div>
            </div>
            <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
              <div className="h-full rounded-full transition-all duration-500"
                style={{
                  width: `${getPct(licence.nb_utilisateurs_actifs, licence.max_utilisateurs)}%`,
                  background: getBarColor(getPct(licence.nb_utilisateurs_actifs, licence.max_utilisateurs))
                }} />
            </div>
          </div>

          {/* Employés */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg flex items-center justify-center"
                  style={{ background: '#fed77415' }}>
                  <Users size={15} style={{ color: '#d97706' }} />
                </div>
                <span className="text-sm font-semibold text-gray-700">Employés</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-black text-gray-900">
                  {licence.nb_employes_actifs}
                </span>
                <span className="text-xs text-gray-400">/ {licence.max_employes}</span>
                <span className="text-xs font-bold px-2 py-0.5 rounded-full"
                  style={{
                    background: getBarColor(getPct(licence.nb_employes_actifs, licence.max_employes)) + '15',
                    color: getBarColor(getPct(licence.nb_employes_actifs, licence.max_employes))
                  }}>
                  {getPct(licence.nb_employes_actifs, licence.max_employes)}%
                </span>
              </div>
            </div>
            <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
              <div className="h-full rounded-full transition-all duration-500"
                style={{
                  width: `${getPct(licence.nb_employes_actifs, licence.max_employes)}%`,
                  background: getBarColor(getPct(licence.nb_employes_actifs, licence.max_employes))
                }} />
            </div>
          </div>

        </div>
      </div>

      {/* ── Alerte expiration proche ── */}
      {licence.statut === 'expire_bientot' && (
        <div className="rounded-2xl p-5 border-2 flex items-start gap-3"
          style={{ backgroundColor: '#fffbeb', borderColor: '#fde68a' }}>
          <AlertCircle size={20} style={{ color: '#d97706' }} className="flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-sm font-bold text-gray-900 mb-1">Licence expire bientôt</h3>
            <p className="text-sm text-gray-600">
              Il vous reste <strong>{licence.jours_restants} jours</strong> avant l'expiration.
              Contactez le support HoraPilot pour renouveler votre abonnement.
            </p>
          </div>
        </div>
      )}

      {/* ── Alerte expirée ── */}
      {licence.statut === 'expiree' && (
        <div className="rounded-2xl p-5 border-2 flex items-start gap-3"
          style={{ backgroundColor: '#fef2f2', borderColor: '#fecaca' }}>
          <XCircle size={20} className="text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-sm font-bold text-gray-900 mb-1">Licence expirée</h3>
            <p className="text-sm text-gray-600">
              Votre licence a expiré le <strong>{formatDate(licence.date_fin)}</strong>.
              Contactez votre administrateur pour renouveler votre abonnement.
            </p>
          </div>
        </div>
      )}

    </div>
  );
};

export default LicenceSection;